import { type RefObject, useCallback, useEffect, useMemo, useState } from "react";

interface Viewport {
  w: number;
  h: number;
  zoom: number;
  pan: { x: number; y: number };
}

interface ViewportControls {
  viewport: Viewport;
  setPanDelta: (next: { x: number; y: number }) => void;
  panDelta: { x: number; y: number };
  bumpZoom: (dir: 1 | -1) => void;
}

function autoFit(w: number, h: number, cols: number, rows: number): number {
  if (w <= 0 || h <= 0) return 1;
  return Math.max(1, Math.min(Math.floor(w / cols), Math.floor(h / rows)));
}

/**
 * Tracks the wrap element's pixel size (via ResizeObserver) and derives the
 * pixi viewport (zoom + pan) from auto-fit plus user wheel/drag overrides.
 */
export function useViewport(
  wrapRef: RefObject<HTMLDivElement | null>,
  cols: number,
  rows: number,
): ViewportControls {
  const [size, setSize] = useState<{ w: number; h: number }>({ w: 0, h: 0 });
  const [zoomOverride, setZoomOverride] = useState<number | null>(null);
  const [panDelta, setPanDelta] = useState<{ x: number; y: number }>({ x: 0, y: 0 });

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;
      const w = Math.max(1, Math.floor(entry.contentRect.width));
      const h = Math.max(1, Math.floor(entry.contentRect.height));
      setSize((prev) => (prev.w === w && prev.h === h ? prev : { w, h }));
    });
    ro.observe(el);
    return () => {
      ro.disconnect();
    };
  }, [wrapRef]);

  const viewport: Viewport = useMemo(() => {
    if (size.w <= 0 || size.h <= 0) return { w: size.w, h: size.h, zoom: 1, pan: { x: 0, y: 0 } };
    const zoom = zoomOverride ?? autoFit(size.w, size.h, cols, rows);
    const pixelW = cols * zoom;
    const pixelH = rows * zoom;
    const baseX = Math.floor((size.w - pixelW) / 2);
    const baseY = Math.floor((size.h - pixelH) / 2);
    return { w: size.w, h: size.h, zoom, pan: { x: baseX + panDelta.x, y: baseY + panDelta.y } };
  }, [size, cols, rows, zoomOverride, panDelta]);

  const bumpZoom = useCallback((dir: 1 | -1) => {
    setZoomOverride((prev) => {
      const base = prev ?? autoFit(size.w, size.h, cols, rows);
      return Math.max(1, Math.min(48, base + dir));
    });
    setPanDelta({ x: 0, y: 0 });
  }, [size, cols, rows]);

  return { viewport, panDelta, setPanDelta, bumpZoom };
}
